import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { useCurrency } from '../../features/currency/CurrencyContext';

/**
 * Compact UGX / USD switcher for header and agent tools.
 */
const CurrencySwitcher = ({ className = '', compact = false }) => {
  const { currency, setCurrency, currencyCodes, currencies, usdToUgx } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-border bg-background hover:bg-muted/60 transition-colors font-semibold text-foreground ${
          compact ? 'px-2 py-1.5 text-xs' : 'px-2.5 py-2 text-sm'
        }`}
        aria-label="Select currency"
        title={`Display currency · 1 USD ≈ ${Number(usdToUgx).toLocaleString()} UGX`}
      >
        <Icon name="Coins" size={compact ? 14 : 16} className="text-primary" />
        <span>{currency}</span>
        <Icon
          name="ChevronDown"
          size={14}
          className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-52 rounded-xl border border-border bg-card shadow-lg z-50 overflow-hidden animate-slide-down">
          <div className="px-3 py-2 border-b border-border bg-muted/40">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Display currency
            </p>
          </div>
          <ul className="py-1">
            {currencyCodes.map((code) => {
              const meta = currencies[code];
              const active = currency === code;
              return (
                <li key={code}>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrency(code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                      active
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-8 text-xs font-bold tabular-nums">{code}</span>
                      <span className="text-xs text-muted-foreground">{meta.name}</span>
                    </span>
                    {active && <Icon name="Check" size={14} />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-3 py-2 border-t border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              1 USD ≈ {Number(usdToUgx).toLocaleString()} UGX · prices convert live
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySwitcher;
